import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Slide, Lightbox } from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import { Helmet } from 'react-helmet-async';

import styles from './Feature.module.css';

import { useFeatureArticle } from './Feature.hooks';
import { FeatureArticleContext } from './Feature.context';
import { Menu } from './menu/Menu';
import { Button } from './menu/Button';
import { Article, NavbarIconLogo } from '../../constants';
import { AppContext } from '../../App.context';
import { Icon } from '../../components/icon/Icon';
import { useFont } from '../issue/Issue.hooks';
import { useArticleHead, useBackUrl } from '../../App.hooks';
import { Loading } from '../../components/loading/Loading';
import { VirtualArticle } from '../../components/article/virtual/VirtualArticle';
import { useStyle } from '../../components/article/virtual/VirtualArticles';
import { LightboxArgs } from '../../components/article/virtual/VirtualArticleContext';
import { useTrackingPageView } from '../../tracking/Tracking.hooks';
import { AssetsContext } from '../../assets/Assets.context';

const Feature = () => {
	const { fontSize, setFontSize } = useFont();
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const [article, setArticle] = useState<Article | null>(null);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	return (
		<FeatureArticleContext.Provider
			value={{
				fontSize,
				setFontSize,
				isMenuOpen,
				setIsMenuOpen,
				article,
				setArticle,
				toggleMenu,
			}}
		>
			<div className={styles['container']}>
				<View />
				<Menu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
					<Button.Share />
					<Button.Font />
				</Menu>
			</div>
		</FeatureArticleContext.Provider>
	);
};

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
		</div>
	);
}

function Topbar() {
	return (
		<div className={styles['topbar']}>
			<div>
				<BackButton />
			</div>
			<div className={styles['title']}>
				<NavbarIconLogo />
			</div>
			<div>
				<MenuButton />
			</div>
		</div>
	);
}

function BackButton() {
	const url = useBackUrl();
	return (
		<button id="back-button" type="button" title="Back">
			<Link to={url}>
				<Icon id={'back-btn'} name={'arrow-left'} color={'#333'} />
			</Link>
		</button>
	);
}

function MenuButton() {
	const { toggleMenu } = useContext(FeatureArticleContext);
	return (
		<button
			type="button"
			title="Menu"
			onClick={() => {
				toggleMenu();
			}}
		>
			<Icon id={'menu-btn'} name={'more-horizontal'} color={'#3d3d3d'} />
		</button>
	);
}

function Content() {
	const { issueId, articleId } = useParams();
	const { lang } = useContext(AppContext);
	const { article, isLoading, error } = useFeatureArticle({
		articleId,
		issueId,
	});
	useArticleHead({ article, lang });

	if (isLoading) {
		return <Loading />;
	}

	if (error) {
		return <ErrorView mesage={error.message} />;
	}

	return <ArticleContainer article={article} />;
}

function ErrorView({ mesage }: ErrorViewProps) {
	return (
		<div className={styles['error']}>
			<p>{mesage}</p>
		</div>
	);
}

interface ErrorViewProps {
	mesage: string;
}

function ArticleContainer({ article }: ArticleContainerProps) {
	const location = useLocation();
	const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
	const [lighboxSlides, setLighboxSlides] = useState<Array<Slide>>([]);
	const { lang, device } = useContext(AppContext);
	const { stylesMap } = useContext(AssetsContext);
	const { fontSize } = useContext(FeatureArticleContext);
	const [content, setContent] = useState<any>(null);
	const { css, classNameMap } = useStyle([article], stylesMap);

	const onLighboxOpen = (args: LightboxArgs) => {
		const { src, caption } = args;
		console.log(`I OPEN LIGHTBOX HERE`, args);
		setLighboxSlides([
			{
				src,
				description: caption,
			},
		]);
		setIsLightboxOpen(true);
	};

	useTrackingPageView({
		title: article?.name[lang],
		location: location.pathname,
	});

	useEffect(() => {
		if (!css) {
			return;
		}
		if (!article) {
			return;
		}
		setTimeout(() => {
			setContent(
				<VirtualArticle
					article={article}
					onLighboxOpen={onLighboxOpen}
					index={0}
					lang={lang}
					isSelected={true}
					device={device?.type || 'mobile'}
					className={classNameMap.get(`${article.id}`)}
					setNavbarVisibility={() => {}}
				/>,
			);
		}, 100);
	}, [css]);

	if (!article) {
		return <div className={styles['not-found']}>Article not found</div>;
	}

	return (
		<div
			className={styles['content']}
			style={{ fontSize: fontSize ? `${fontSize}%` : undefined }}
		>
			<HeadHelmet article={article} />
			{content}
			<Lightbox
				carousel={{ finite: lighboxSlides.length <= 1 }}
				plugins={[Captions, Zoom, Fullscreen]}
				open={isLightboxOpen}
				close={() => setIsLightboxOpen(false)}
				slides={lighboxSlides}
				render={{
					buttonPrev:
						lighboxSlides.length <= 1 ? () => null : undefined,
					buttonNext:
						lighboxSlides.length <= 1 ? () => null : undefined,
				}}
			/>
			<style>{css}</style>
		</div>
	);
}

interface ArticleContainerProps {
	article: Article | null;
}

function HeadHelmet({ article }: ArticleContainerProps) {
	const { lang } = useContext(AppContext);
	if (!article) {
		return null;
	}
	const { name, thumbnail } = article;
	const keywords =
		article?.keywords && article.keywords.length ? article.keywords : [];
	const keywordsString = [...new Set(keywords)].join(',');
	return (
		<Helmet>
			<meta property="og:type" content={'article'} />
			{name?.[lang] ? (
				<meta property="og:title" content={name?.[lang]} />
			) : null}
			<meta property="og:url" content={window.location.href} />
			{thumbnail ? (
				<meta property="og:image" content={thumbnail} />
			) : null}
			<meta property="og:category" content="digital-editions" />
			{keywordsString.length ? (
				<meta name="keywords" content={keywordsString}></meta>
			) : null}
			{keywordsString.length ? (
				<meta name="post-tags" content={keywordsString}></meta>
			) : null}
		</Helmet>
	);
}

export default Feature;
